import React, { useMemo, useState } from 'react'
import { FormControlLabel } from '@mui/material'
import * as S from './ConfirmPrintsBody.style'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { actions } from 'src/features/receivement/ReceivementState'
import { PrintInvoicesStepsEnum } from '../PrintInvoicesSection.types'
import { useForm } from 'react-hook-form'
import { handleOnKeyDown } from 'src/utils/Form'
import { toast } from 'react-toastify'

interface ConfirmPrintsBodyProps {
  badge: number
  idReceivement?: number
  dataToPrint?: any
  onConfirmSuccess?: (data: any) => void
}

interface InvoicesToSign {
  number: number
  accessKey: string
  isRejected: boolean
  biped: boolean
  isSigned: boolean | null
}

interface InvoiceActionProps {
  text: string
  textColor: string
}

export const ConfirmPrintsBody: React.FC<ConfirmPrintsBodyProps> = ({
  badge,
  idReceivement,
  dataToPrint,
  onConfirmSuccess,
}) => {
  const invoicesReturn = useSelector((state) => state.receivement.receivementInvoices?.invoicesReturn) || []
  const invoicesAccepted = useSelector((state) => state.receivement.receivementInvoices?.invoices)
  const invoicesRejected = useSelector((state) => state.receivement.receivementInvoices?.invoicesRejected)
  const dispatch = useDispatch()
  const reprintInvoices = () => dispatch(actions.setCurrentPrintInvoicesStep(PrintInvoicesStepsEnum.START_PRINT_OR_RETURN))
  const [invoiceReturnChecked, setInvoiceReturnChecked] = useState(false)
  const [bipedAccessKeys, setBipedAccessKeys] = useState<string[]>([])

  const signInvoices: InvoicesToSign[] = useMemo(() => {
    const invs = invoicesAccepted || []
    const invsRejected = invoicesRejected || []
    return [
      ...invs.map((invoice: any) => ({
        number: invoice.invoiceNumber,
        accessKey: invoice.invoiceAccessKey,
        isRejected: false,
        isSigned: invoice.isSigned,
        biped: invoice.isSigned !== null || bipedAccessKeys.includes(invoice.invoiceAccessKey),
      })),
      ...invsRejected.map((invoice: any) => ({
        number: invoice.invoiceNumber,
        accessKey: invoice.invoiceAccessKey,
        isRejected: true,
        isSigned: invoice.isSigned,
        biped: invoice.isSigned !== null || bipedAccessKeys.includes(invoice.invoiceAccessKey),
      })),
    ]
  }, [bipedAccessKeys, invoicesAccepted, invoicesRejected])
  const [awaitingInvoiceConfirmation, setAwaitingInvoiceConfirmation] = useState(false)
  const [invoiceAccessKeyFocused, setInvoiceAccessKeyFocused] = useState(false)

  const {
    register, setValue, setError, setFocus, getValues, clearErrors, formState: { errors },
  } = useForm<{ invoiceAccessKey: string }>()

  const resolveConfirmButtonIsDisabled = useMemo(() => {
    const hasInvoiceNotSigned = signInvoices.some((invoice) => invoice.isSigned === null)

    return (signInvoices.length > 0 && invoicesReturn.length > 0 && (hasInvoiceNotSigned || !invoiceReturnChecked))
    || (signInvoices.length > 0 && invoicesReturn.length === 0 && hasInvoiceNotSigned)
    || (signInvoices.length === 0 && invoicesReturn.length > 0 && !invoiceReturnChecked)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInvoices, invoiceReturnChecked])

  const goToAttendanceTab = () => {
    if (onConfirmSuccess) {
      onConfirmSuccess(dataToPrint)
    } else {
      dispatch(actions.releaseReceivement({ idReceivement, badge }))
    }
  }

  const getInvoiceAction = (confirmInvoice: InvoicesToSign): InvoiceActionProps => {
    if (confirmInvoice.biped) {
      if (confirmInvoice.isRejected) {
        return { text: 'NÃO ASSINAR', textColor: '#df0000' }
      }
      return { text: 'ASSINAR', textColor: '#08ca08' }
    }
    return { text: 'Bipar chave de acesso', textColor: '#000' }
  }

  const handleSubmitInvoiceAccessKey = () => {
    clearErrors('invoiceAccessKey')

    const invoiceAccessKey = getValues('invoiceAccessKey')

    if (!invoiceAccessKey) {
      return
    }

    const invoiceIndex = signInvoices.findIndex((invoice) => invoice.accessKey === invoiceAccessKey)

    if (invoiceIndex === -1) {
      setError('invoiceAccessKey', { message: 'Chave de acesso não encontrada' })
      return
    }

    if (signInvoices[invoiceIndex].isSigned !== null) {
      setError('invoiceAccessKey', { message: 'Nota já confirmada' })
      return
    }

    setBipedAccessKeys((prev) => [...prev, invoiceAccessKey])
    setAwaitingInvoiceConfirmation(true)
    toast.info('Confirme a ação da nota fiscal clicando no botão OK')
  }

  const handleConfirmInvoiceAction = (index: number) => {
    const onSuccess = () => {
      setAwaitingInvoiceConfirmation(false)
      setValue('invoiceAccessKey', '')
      if (signInvoices.some((invoice) => invoice.isSigned === null)) {
        setFocus('invoiceAccessKey')
      }
      toast.success('Ação confirmada com sucesso')
    }

    const invoice = signInvoices[index]
    dispatch(actions.signAuthorization({
      accessKey: invoice.accessKey,
      onSuccess,
    }))
  }

  return (
    <S.Container>
      {invoicesReturn.length > 0 && (
        <FormControlLabel
          data-testid='invoice-return-checkbox-test-id'
          control={
            <S.ConfirmCheckbox onChange={(event) => setInvoiceReturnChecked(event.target.checked)} />
          }
          label={
            `${invoicesReturn.length} ${invoicesReturn.length > 1 ? 'CANHOTOS DAS NOTAS DE DEVOLUÇÃO ASSINADOS PELO FORNECEDOR' : 'CANHOTO DA NOTA DE DEVOLUÇÃO ASSINADO PELO FORNECEDOR'}`
          }
        />
      )}
      {signInvoices.length > 0 && (
      <S.InvoiceListContainer>
        <S.InvoiceListTitleContainer>
          <S.InvoiceListTitle>
            Notas fiscais fornecedor
          </S.InvoiceListTitle>
          <S.InvoiceListTitle>
            {`${signInvoices.filter((invoice) => invoice.isSigned !== null).length}/${signInvoices.length}`}
          </S.InvoiceListTitle>
        </S.InvoiceListTitleContainer>
        <S.List>
          {signInvoices.map((invoice, index) => (
            <S.ListItem key={index} data-testid='invoice-list-item-test-id'>
              <S.ListItemContainer>
                <S.ListItemText data-testid='list-item-invoice-number-test-id'>
                  Nr: {invoice.number}
                </S.ListItemText>
                <S.ListItemText data-testid='list-item-invoice-access-key-test-id'>
                  Chave: {invoice.accessKey}
                </S.ListItemText>
                <S.ListItemText data-testid='list-item-invoice-action-test-id'>
                  {'Ação: '}
                  <S.ListItemText textColor={getInvoiceAction(invoice).textColor} data-testid='list-item-invoice-action-text-test-id'>
                    {getInvoiceAction(invoice).text}
                  </S.ListItemText>
                </S.ListItemText>
                {invoice.biped && (
                <S.OkButton
                  title='OK'
                  variant='contained'
                  size='small'
                  onClick={() => handleConfirmInvoiceAction(index)}
                  data-testid='list-item-confirm-action-button-test-id'
                  disabled={invoice.isSigned !== null}
                />
                )}
              </S.ListItemContainer>
            </S.ListItem>
          ))}
        </S.List>
        {signInvoices.some((invoice) => invoice.isSigned === null) && (
          <S.CustomTextField
            id='invoiceAccessKey'
            name='invoiceAccessKey'
            type='text'
            autoComplete='off'
            autoFocus
            inputProps={{
              ...register('invoiceAccessKey'),
              'data-testid': 'invoice-access-key-input-test-id',
            }}
            error={!!errors.invoiceAccessKey}
            label='Bipe a chave de acesso'
            helperText={errors.invoiceAccessKey?.message}
            fullWidth
            onKeyDown={(event) => handleOnKeyDown(event, '', handleSubmitInvoiceAccessKey)}
            disabled={awaitingInvoiceConfirmation}
            InputLabelProps={{ shrink: invoiceAccessKeyFocused || !!getValues('invoiceAccessKey') }}
            onFocus={() => setInvoiceAccessKeyFocused(true)}
            onBlur={() => setInvoiceAccessKeyFocused(false)}
            testId='invoice-access-key-input-test-id'
          />
        )}
      </S.InvoiceListContainer>
      )}
      <S.ButtonsContainer>
        <S.ConfirmButton
          variant='contained'
          size='large'
          type='button'
          disabled={resolveConfirmButtonIsDisabled}
          onClick={goToAttendanceTab}
          data-testid='confirm-prints-button-test-id'
        >
          Confirmar
        </S.ConfirmButton>
        <S.RePrintButton
          variant='contained'
          size='large'
          type='button'
          onClick={reprintInvoices}
          data-testid='reprint-invoices-button-test-id'
        >
          Reimprimir
        </S.RePrintButton>
      </S.ButtonsContainer>
    </S.Container>
  )
}
